// R-162 — Cross-batch transition helper.
// Compares the current document's batchId to the previous one and dispatches
// by step group (per @capture/shared classifyTransition):
//   flow (key/verify/post-ocr) → fires onFlowTransition (caller renders toast)
//   qc   (qa/supervisor)        → opens the confirm modal
//   other (scan/classify/export) → no-op

import { useEffect, useRef, useState } from "react";
import { classifyTransition } from "@capture/shared";

export interface UseBatchTransitionEffectArgs {
  currentBatchId: string | null;
  currentBatchDisplayId: string | null;
  currentStep: string;
  /** Called when a flow-step transition fires the toast. */
  onFlowTransition: (toBatchLabel: string) => void;
  /** Called when the operator confirms a QC transition. */
  onQcTransitionAccept: () => void;
  /** Called when the operator cancels a QC transition (e.g. Stop). */
  onQcTransitionCancel: () => void;
}

export interface BatchTransitionState {
  qcModalOpen: boolean;
  qcFromBatch: string | null;
  qcToBatch: string | null;
  acceptQc: () => void;
  cancelQc: () => void;
}

export function useBatchTransitionEffect({
  currentBatchId,
  currentBatchDisplayId,
  currentStep,
  onFlowTransition,
  onQcTransitionAccept,
  onQcTransitionCancel,
}: UseBatchTransitionEffectArgs): BatchTransitionState {
  const prevBatchIdRef = useRef<string | null>(null);
  const prevBatchDisplayIdRef = useRef<string | null>(null);
  const [qcModalOpen, setQcModalOpen] = useState(false);
  const [qcFromBatch, setQcFromBatch] = useState<string | null>(null);
  const [qcToBatch, setQcToBatch] = useState<string | null>(null);

  useEffect(() => {
    const prev = prevBatchIdRef.current;
    const next = currentBatchId;

    // First mount: capture baseline, no transition
    if (prev === null || next === null) {
      prevBatchIdRef.current = next;
      prevBatchDisplayIdRef.current = currentBatchDisplayId;
      return;
    }

    if (prev !== next) {
      const group = classifyTransition(currentStep);
      if (group === "flow") {
        onFlowTransition(currentBatchDisplayId ?? next);
      } else if (group === "qc") {
        setQcFromBatch(prevBatchDisplayIdRef.current);
        setQcToBatch(currentBatchDisplayId ?? next);
        setQcModalOpen(true);
      }
    }

    prevBatchIdRef.current = next;
    prevBatchDisplayIdRef.current = currentBatchDisplayId;
  }, [currentBatchId, currentBatchDisplayId, currentStep, onFlowTransition]);

  return {
    qcModalOpen,
    qcFromBatch,
    qcToBatch,
    acceptQc: () => {
      setQcModalOpen(false);
      onQcTransitionAccept();
    },
    cancelQc: () => {
      setQcModalOpen(false);
      onQcTransitionCancel();
    },
  };
}
